import { z } from 'zod';
import { BusinessDocType, FieldDictionaryRecordType } from '../../types';

const purchaseOrderOptionsSchema = z.object({
    businessDocType: z.literal(BusinessDocType.PurchaseOrder),
    configuration: z.object({
        recordType: z.literal(FieldDictionaryRecordType.BuyerLevelOverride),
    }),
});

const asnOptionsSchema = z.object({
    businessDocType: z.literal(BusinessDocType.ASNGeneric),
    configuration: z.object({
        recordType: z.literal(FieldDictionaryRecordType.BuyerLevelOverride),
    }),
});

const purchaseOrderAcknowledgementOptionsSchema = z.object({
    businessDocType: z.literal(BusinessDocType.PurchaseOrderAcknowledgement),
    configuration: z.object({
        recordType: z.literal(FieldDictionaryRecordType.BuyerLevelOverride),
    }),
});

const invoiceBuyerLevelOverrideOptionsSchema = z.object({
    recordType: z.literal(FieldDictionaryRecordType.BuyerLevelOverride),
});

const invoiceWorkflowLevelOverrideOptionsSchema = z.object({
    recordType: z.literal(FieldDictionaryRecordType.WorkflowLevelOverride),
    /**
     * The id of the workflow field dictionary template.
     * Used as base for the field dictionary changes in create mode.
     * In case of update mode, the field dictionary changes are linked
     * to this template as workflow level override.
     */
    workflowFieldDictionaryTemplateId: z.coerce.number().optional(),
});

const invoiceOptionsSchema = z.object({
    businessDocType: z.literal(BusinessDocType.Invoice),
    configuration: z.discriminatedUnion('recordType', [
        invoiceBuyerLevelOverrideOptionsSchema,
        invoiceWorkflowLevelOverrideOptionsSchema,
    ]),
});

const businessDocTypeSpecificOptionsSchema = z.discriminatedUnion('businessDocType', [
    purchaseOrderOptionsSchema,
    asnOptionsSchema,
    purchaseOrderAcknowledgementOptionsSchema,
    invoiceOptionsSchema,
]);

export const fieldDictionaryEditorToolConfigurationSchema = z
    .object({
        targetBuyerCompanyId: z.coerce.number({ errorMap: () => ({ message: 'Buyer Company is required.' }) }),
        targetSupplierCompanyId: z.coerce.number().optional(),
        isPrintPreview: z.boolean().default(false),
    })
    .and(businessDocTypeSpecificOptionsSchema)
    .superRefine((data, ctx) => {
        if (data.isPrintPreview && data.configuration.recordType !== FieldDictionaryRecordType.BuyerLevelOverride) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Print preview is only supported for buyer-level override.',
            });
        }
    });

export type FieldDictionaryEditorToolConfiguration = z.infer<typeof fieldDictionaryEditorToolConfigurationSchema>;
